import React, { useState } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  TextField,
  useTheme,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Paper
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CommentIcon from '@mui/icons-material/Comment';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import AllocationKeyModal from "../approvalPackages/AllocationKeyModal";
import InvoiceApprovalActionsStyles from '../../../styles/purchaseInvoices/invoiceManagement/InvoiceApprovalActionsStyles';
import useInvoiceApproval from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceApproval';
import PriorityRetrySection from './PriorityRetrySection';
import useBranches from '../../../hooks/useBranches';
import { useUser } from '../../../contexts/UserContext';
import useGroupManagers from '../../../hooks/purchaseInvoices/invoiceManagement/useGroupManagers';
import useClarification from '../../../hooks/purchaseInvoices/invoiceManagement/useClarification';
import ClarificationResponsePanel from './ClarificationResponsePanel';
import ClarificationHistory from './ClarificationHistory';

const InvoiceApprovalActions = ({
  invoice,
  onApprove,
  onReject,
  onTransferApprover,
  isProcessing,
  onNavigateToComparison,
  onNavigateToComments,
  initiateApproval,
  allocationKeyModalOpen,
  setAllocationKeyModalOpen,
  allocationKeys,
  isLoadingAllocationKeys,
  allocationKeysError,
  currentInvoiceYear,
  completeApprovalWithAllocationKey,
  isInvoicePriceHigher,
  availableApprovers,
  isLoadingApprovers,
  isTransferringFromParent = false,
  updateInvoiceOptimistically = null
}) => {
  const theme = useTheme();
  const { user } = useUser();

  const { companies, profitCenters, isLoading: isLoadingBranches } = useBranches();
  const invoiceGroupId = invoice?.groupSettings?._id || invoice?.groupSettings || invoice?.groupId;
  const { groupManagers, isLoading: isLoadingGroupManagers } = useGroupManagers(invoiceGroupId);

  
  const { 
    sendToClarification, 
    isSendingClarification,
    clarifications,
    hasPendingClarification 
  } = useClarification(invoice?.id);
  const [showClarificationSection, setShowClarificationSection] = useState(false);
  const [selectedITManager, setSelectedITManager] = useState('');
  const [clarificationMessage, setClarificationMessage] = useState('');

  const userEmail = user?.email?.toLowerCase() || user?.Email?.toLowerCase();
  const pendingClarificationForUser = clarifications?.find(
    c => c.status === 'pending' && c.assignedTo?.email?.toLowerCase() === userEmail
  );

  const {
    rejectionReason,
    setRejectionReason,
    handleApprove,
    handleReject,
    userHasApprovalPermission,
    canApproveThisInvoice,
    invoicePriceHigher,
    showTransferSection,
    selectedApprover,
    setSelectedApprover,
    handleTransferApprover,
    toggleTransferSection,
    canTransferApprover,
    isProcessingInternal
  } = useInvoiceApproval(
    invoice,
    onApprove,
    onReject,
    initiateApproval,
    onTransferApprover,
    updateInvoiceOptimistically
  );

  const isTransferring = isTransferringFromParent;
  const isProcessingApproval = isProcessingInternal;

  if (isTransferring) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Card sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h6" color="info.main" sx={{ mb: 2 }}>
            מעביר מאשר...
          </Typography>
          <CircularProgress color="info" size={48} />
        </Card>
      </Box>
    );
  }

  const TransferApproverSection = () => (
    canTransferApprover && (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SwapHorizIcon />}
            onClick={toggleTransferSection}
            disabled={isProcessingApproval || isTransferring}
            sx={InvoiceApprovalActionsStyles.transferApproverButton}
          >
            {showTransferSection ? 'ביטול העברה' : '🔄 העברה למאשר אחר'}
          </Button>
        </Box>

        <Collapse in={showTransferSection}>
          <Box sx={InvoiceApprovalActionsStyles.transferSectionContainer}>
            <Box sx={InvoiceApprovalActionsStyles.sectionHeader()}>
              <Box sx={InvoiceApprovalActionsStyles.sectionIconBox('#1976d2', '#42a5f5')}>
                <SwapHorizIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={InvoiceApprovalActionsStyles.sectionTitle('#1565c0')}>
                העברת מאשר
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2 }}>
              <FormControl sx={{ minWidth: 250, maxWidth: 400 }}>
                <InputLabel sx={{ backgroundColor: '#e3f2fd', px: 0.5 }}>בחר מאשר חדש</InputLabel>
                <Select
                  value={selectedApprover}
                  onChange={(e) => setSelectedApprover(e.target.value)}
                  label="בחר מאשר חדש"
                  disabled={isLoadingApprovers || isTransferring}
                  sx={InvoiceApprovalActionsStyles.modernSelect('#64b5f6', '#42a5f5', '#1976d2', '#e3f2fd')}
                >
                  {isLoadingApprovers ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} /> טוען מאשרים...
                    </MenuItem>
                  ) : availableApprovers?.length === 0 ? (
                    <MenuItem disabled>לא נמצאו מאשרים זמינים</MenuItem>
                  ) : (
                    availableApprovers
                      ?.filter(approver => {
                        if (canApproveThisInvoice && user?._id === approver.id) {
                          return false;
                        }
                        return true;
                      })
                      .map((approver) => (
                        <MenuItem key={approver.id} value={approver.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={InvoiceApprovalActionsStyles.dropdownAvatar('#1976d2')}>
                              {approver.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </Box>
                            <Typography variant="body2" fontWeight="medium">{approver.name}</Typography>
                          </Box>
                        </MenuItem>
                      ))
                  )}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleTransferApprover}
                disabled={!selectedApprover || isTransferring || isLoadingApprovers}
                startIcon={isTransferring ? <CircularProgress size={18} color="inherit" /> : <SwapHorizIcon />}
                sx={InvoiceApprovalActionsStyles.gradientActionButton('#1976d2', '#42a5f5', 'rgba(25, 118, 210, 0.35)')}
              >
                {isTransferring ? 'מעביר...' : '✓ אשר העברה'}
              </Button>
            </Box>
          </Box>
        </Collapse>

        <Divider sx={{ mb: 2 }} />
      </>
    )
  );

  const handleSendClarification = () => {
    if (!selectedITManager || !invoice?.approvalPackage?._id) return;
    
    sendToClarification({
      invoiceId: invoice.id,
      approvalPackageId: invoice.approvalPackage._id,
      itManagerId: selectedITManager,
      message: clarificationMessage
    });
    
    setSelectedITManager('');
    setClarificationMessage('');
    setShowClarificationSection(false);
  };

  const toggleClarificationSection = () => {
    setShowClarificationSection(!showClarificationSection);
    if (!showClarificationSection) {
      setSelectedITManager('');
      setClarificationMessage('');
    }
  };

 
  const canSendToClarification = canTransferApprover && !hasPendingClarification;
  const hasGroupManagers = groupManagers?.length > 0;

  const clarificationSectionJSX = canSendToClarification && (
    <>
        {hasGroupManagers ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2, gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={toggleClarificationSection}
                disabled={isProcessingApproval || isSendingClarification || hasPendingClarification}
                sx={InvoiceApprovalActionsStyles.clarificationButton}
              >
                {showClarificationSection ? 'ביטול' : '💬 שלח לבירור'}
              </Button>
              
              {hasPendingClarification && (
                <Chip 
                  label="⏳ בירור בתהליך" 
                  sx={InvoiceApprovalActionsStyles.clarificationChip}
                />
              )}
            </Box>

            <Collapse in={showClarificationSection}>
              <Box sx={InvoiceApprovalActionsStyles.clarificationSectionContainer}>
                <Box sx={InvoiceApprovalActionsStyles.sectionHeader()}>
                  <Box sx={InvoiceApprovalActionsStyles.sectionIconBox('#ff9800', '#f57c00')}>
                    <HelpOutlineIcon sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={InvoiceApprovalActionsStyles.sectionTitle('#e65100')}>
                    שליחה לבירור
                  </Typography>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={InvoiceApprovalActionsStyles.clarificationDescription}>
                  בחר מנהל קבוצה לשליחת בקשת בירור. המנהל יקבל מייל עם פרטי החשבונית ויוכל להגיב.
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2 }}>
                  <FormControl sx={{ minWidth: 300, maxWidth: 450 }}>
                    <InputLabel sx={{ backgroundColor: '#fff8e1', px: 0.5 }}>בחר מנהל קבוצה</InputLabel>
                    <Select
                      value={selectedITManager}
                      onChange={(e) => setSelectedITManager(e.target.value)}
                      label="בחר מנהל קבוצה"
                      disabled={isLoadingGroupManagers || isSendingClarification}
                      sx={InvoiceApprovalActionsStyles.modernSelect('#ffb74d', '#ff9800', '#f57c00', '#fff8e1')}
                    >
                      {isLoadingGroupManagers ? (
                        <MenuItem disabled>
                          <CircularProgress size={16} sx={{ mr: 1 }} /> טוען מנהלים...
                        </MenuItem>
                      ) : groupManagers?.length === 0 ? (
                        <MenuItem disabled>לא נמצאו מנהלים לקבוצה זו</MenuItem>
                      ) : (
                        groupManagers.map((manager) => (
                          <MenuItem key={manager.id} value={manager.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={InvoiceApprovalActionsStyles.dropdownAvatar('#ff9800')}>
                                {manager.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">{manager.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{manager.email}</Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  <TextField
                    label="הודעה למנהל (אופציונלי)"
                    multiline
                    rows={3}
                    value={clarificationMessage}
                    onChange={(e) => setClarificationMessage(e.target.value)}
                    placeholder="כתוב כאן הודעה או שאלה למנהל הקבוצה..."
                    disabled={isSendingClarification}
                    sx={{ 
                      maxWidth: 450,
                      ...InvoiceApprovalActionsStyles.modernTextField('#ffb74d', '#ff9800', '#f57c00')
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSendClarification}
                    disabled={!selectedITManager || isSendingClarification || isLoadingGroupManagers}
                    startIcon={isSendingClarification ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    sx={{ 
                      alignSelf: 'flex-start',
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      ...InvoiceApprovalActionsStyles.gradientActionButton('#ff9800', '#f57c00', 'rgba(255, 152, 0, 0.35)')
                    }}
                  >
                    {isSendingClarification ? 'שולח...' : '📤 שלח לבירור'}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </>
        ) : !isLoadingGroupManagers && (
          <Box sx={{ mb: 2 }}>
            <Paper elevation={0} sx={InvoiceApprovalActionsStyles.infoPaper}>
              <Box sx={InvoiceApprovalActionsStyles.infoIconBox}>
                <InfoIcon sx={{ color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1565c0', mb: 0.5 }}>
                  שליחה לבירור אינה זמינה
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  לא הוגדרו מנהלי קבוצה עבור קבוצה זו. 
                  כדי לאפשר שליחת חשבוניות לבירור, יש להגדיר מנהלים בדף ההגדרות → טאב "מנהלי קבוצות".
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />
      </>
  );

  const canShowApprovalActions = invoice?.status === 'ממתין לאישור' || invoice?.status === 'REQUIRES_LINE_MAPPING' || invoice?.status === 'PENDING_CLARIFICATION';
  const hasRetryableError = invoice?.approvalPackage?.priorityCreationFailed === true && invoice?.approvalPackage?.allowRetry === true;
  
  if (!userHasApprovalPermission || (!canShowApprovalActions && !hasRetryableError)) {
    return (
      <Box sx={InvoiceApprovalActionsStyles.container}>
        <Card sx={InvoiceApprovalActionsStyles.card}>
          <Typography variant="h6" gutterBottom color="primary">
            אישור חשבונית
          </Typography>
          <Box sx={InvoiceApprovalActionsStyles.statusBox}>
            <Typography variant="h6" color="text.secondary">
              סטטוס החשבונית: {invoice?.status || 'לא ידוע'}
            </Typography>

            <TransferApproverSection />
            {clarificationSectionJSX}

            {!userHasApprovalPermission && !pendingClarificationForUser && (
              <Typography variant="body1" color="error" sx={{ mt: 1 }}>
                אין לך הרשאה לאשר או לדחות חשבוניות
              </Typography>
            )}
            {userHasApprovalPermission && !canShowApprovalActions && !hasRetryableError && (
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                החשבונית אינה ממתינה לאישור כרגע
              </Typography>
            )}
          </Box>

          {pendingClarificationForUser && (
            <Box sx={{ mt: 3 }}>
              <ClarificationResponsePanel 
                clarification={pendingClarificationForUser}
              />
            </Box>
          )}

          {hasRetryableError && (
            <Box sx={{ mt: 2 }}>
              <PriorityRetrySection
                invoice={invoice}
                approvalPackage={invoice?.approvalPackage}
              />
            </Box>
          )}
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={InvoiceApprovalActionsStyles.container}>
      <Card sx={InvoiceApprovalActionsStyles.card}>
        <Typography variant="h6" gutterBottom color="primary">
          אישור חשבונית
        </Typography>

        <Box sx={InvoiceApprovalActionsStyles.infoBox}>
          <Typography variant="body1">
            החשבונית ממתינה לאישור שלך. נא לבדוק את פרטי החשבונית ולהשוות מול נתוני ההזמנה לפני אישור.
          </Typography>
          <Button
            variant="text"
            sx={InvoiceApprovalActionsStyles.infoLink}
            onClick={onNavigateToComparison}
          >
            לחץ כאן לצפייה בהשוואת נתונים
          </Button>
        </Box>

        <TransferApproverSection />
        {clarificationSectionJSX}

        {!userHasApprovalPermission && (
          <Box sx={InvoiceApprovalActionsStyles.noPermissionBox}>
            <Typography variant="body1" color="warning.main">
              אין לך הרשאה לאשר או לדחות חשבוניות
            </Typography>
          </Box>
        )}

        {userHasApprovalPermission && !canApproveThisInvoice && (
          <Box sx={InvoiceApprovalActionsStyles.noApprovalBox}>
            <Typography variant="body1" color="error.main">
              אין לך הרשאה לאשר חשבונית זו
            </Typography>
          </Box>
        )}

        {userHasApprovalPermission && canApproveThisInvoice && invoicePriceHigher && (
          <Box sx={InvoiceApprovalActionsStyles.priceTooHighBox}>
            <Typography variant="body1" color="error.main" sx={{ fontWeight: 'bold' }}>
              לא ניתן לאשר - מחיר החשבונית גדול ממחיר ההזמנה
            </Typography>
          </Box>
        )}

        {userHasApprovalPermission && canApproveThisInvoice && !invoicePriceHigher && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={InvoiceApprovalActionsStyles.approveCard}>
                <Box sx={InvoiceApprovalActionsStyles.headerBox}>
                  <ThumbUpIcon color="success" sx={{ fontSize: 28, mr: 1 }} />
                  <Typography variant="h6" color="success.main">
                    אישור החשבונית
                  </Typography>
                </Box>

                <Box sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'success.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  position: 'relative',
                  animation: 'gentle-pulse 3s ease-in-out infinite',
                  boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    backgroundColor: 'success.main',
                    borderRadius: '2px 0 0 2px',
                    animation: 'glow 2s ease-in-out infinite alternate'
                  },
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.2)',
                    transition: 'all 0.3s ease',
                    animation: 'none'
                  },
                  '@keyframes gentle-pulse': {
                    '0%, 100%': {
                      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.3)'
                    },
                    '50%': {
                      boxShadow: '0 0 0 8px rgba(76, 175, 80, 0.1)'
                    }
                  },
                  '@keyframes glow': {
                    '0%': {
                      backgroundColor: 'success.main',
                      opacity: 0.8
                    },
                    '100%': {
                      backgroundColor: 'success.light',
                      opacity: 1
                    }
                  }
                }}>
                  <Box sx={{
                    backgroundColor: 'success.main',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'icon-bounce 2s ease-in-out infinite'
                  }}>
                    <CommentIcon sx={{ color: 'white', fontSize: 16 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{
                      color: 'text.primary',
                      mb: 0.5,
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      💡 ניתן להוסיף הערות לפני האישור
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={onNavigateToComments}
                      sx={{
                        p: 0,
                        fontSize: '0.75rem',
                        color: 'success.dark',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        minHeight: 'auto',
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'transparent',
                          color: 'success.main',
                          transform: 'translateX(-2px)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      לחיצה כאן להוספת הערות ←
                    </Button>
                  </Box>

                  {/* CSS Keyframes for icon bounce */}
                  <Box component="style" sx={{ display: 'none' }}>
                    {`
                      @keyframes icon-bounce {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-2px); }
                      }
                    `}
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 2, flex: 1 }}>
                  אישור החשבונית מעיד על בדיקה מקיפה ואישור תקינות התוכן. האישור מביא להעברה אוטומטית של החשבונית לקליטה במערכת פריוריטי.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  startIcon={isProcessingApproval ? <CircularProgress size={16} color="inherit" /> : <ThumbUpIcon />}
                  onClick={handleApprove}
                  disabled={isProcessingApproval || isTransferring}
                  sx={{
                    mt: 'auto',
                    position: 'relative',
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.success.main + '80',
                      color: 'white'
                    }
                  }}
                >
                  {isProcessingApproval ? 'מאשר חשבונית...' : 'אשר חשבונית'}
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={InvoiceApprovalActionsStyles.rejectCard}>
                <Box sx={InvoiceApprovalActionsStyles.headerBox}>
                  <ThumbDownIcon color="error" sx={{ fontSize: 28, mr: 1 }} />
                  <Typography variant="h6" color="error">
                    דחיית החשבונית
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  אם החשבונית אינה תקינה, ניתן לדחות אותה עם הסבר מדוע.
                </Typography>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="סיבת הדחיה..."
                  sx={{ mb: 2 }}
                  disabled={isProcessingApproval || isTransferring}
                />
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={isProcessingApproval ? <CircularProgress size={16} color="inherit" /> : <ThumbDownIcon />}
                  onClick={handleReject}
                  disabled={isProcessingApproval || !rejectionReason.trim() || isTransferring}
                  sx={{
                    position: 'relative',
                    '&.Mui-disabled': {
                      borderColor: theme.palette.error.main + '80',
                      color: theme.palette.error.main + '80'
                    }
                  }}
                >
                  {isProcessingApproval ? 'דוחה חשבונית...' : 'דחה חשבונית'}
                </Button>
              </Card>
            </Grid>
          </Grid>
        )}
        
        {pendingClarificationForUser && (
          <Box sx={{ mt: 3 }}>
            <ClarificationResponsePanel 
              clarification={pendingClarificationForUser}
            />
          </Box>
        )}

        {clarifications && clarifications.length > 0 && (
          <ClarificationHistory clarifications={clarifications} />
        )}

        <AllocationKeyModal
          open={allocationKeyModalOpen}
          onClose={() => setAllocationKeyModalOpen(false)}
          onSubmit={completeApprovalWithAllocationKey}
          allocationKeys={allocationKeys}
          companies={companies}
          profitCenters={profitCenters}
          isLoading={isLoadingAllocationKeys || isLoadingBranches}
          error={allocationKeysError}
          defaultYear={currentInvoiceYear}
          isSubmitting={isProcessing}
        />

        <PriorityRetrySection
          invoice={invoice}
          approvalPackage={invoice?.approvalPackage}
        />
      </Card>
    </Box>
  );
};

export default InvoiceApprovalActions;